// ui-kit/marks.js: de merk- en icoontekeningen die CRM en portaal delen
// (portaalplan U5). Geen hooks, geen 'use client': het portaal rendert ze ook in
// servercomponenten. EEN definitie per tekening; de CRM leest ze via
// components/HomeV2/lib.js, AgendaRow.js en Button.js (import, geen kopie).
// Isolatie: lib/ui-kit-isolation.test.mjs.

import React from 'react';
import { HV2 } from './tokens.mjs';
import { BRAND_MARKS, pmarkWidth } from './brand-marks.mjs';
import { KPI_ICON_SHAPES } from './kpi-icons.mjs';
import { EntityIcon } from './primitives.js';

// Het P&-merk als inline SVG uit BRAND_MARKS.pmark: geen PNG-masker, dus scherp op
// elke maat en in elke kleur. Zonder title is het decoratief (aria-hidden). De
// title gaat alleen naar aria-label, niet naar een hover-tooltip.
export function PMark({ height = 16, color = 'currentColor', title = 'Pearson & Partners', style }) {
  const m = BRAND_MARKS.pmark;
  return (
    <svg viewBox={m.viewBox} width={pmarkWidth(height)} height={height} fill={color}
      role={title ? 'img' : undefined} aria-label={title || undefined} aria-hidden={title ? undefined : 'true'}
      style={{ flexShrink: 0, display: 'inline-block', ...style }}>
      {m.ds.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

// KPI-vormen uit ui-kit/kpi-icons.mjs als React-elementen (EEN bron met het
// kantoorscherm). MenuIcon en RailIcon in components/HomeV2/lib.js lezen deze.
export const kpiShapes = (key) => (KPI_ICON_SHAPES[key] || []).map(([tag, attrs], i) => React.createElement(tag, { key: i, ...attrs }));

export function KpiIcon({ name, size = 16, color = 'currentColor', strokeWidth = 2, style }) {
  if (!KPI_ICON_SHAPES[name]) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, ...style }}>
      {kpiShapes(name)}
    </svg>
  );
}

// ── Tracks (eigenaarsbesluit 13 augustus 2026) ─────────────────────────────
// EEN universeel systeem voor de drie pipeline-tracks (Interviews, Plaatsingen,
// CV Send). TRACK_META is de ene bron voor kleur, tint en label per track;
// TrackIcon tekent het bijpassende icoon. Sourcing is een APARTE track (eigen
// accentSrc-dot in ProcessCvLanes.js) en hoort hier niet bij.
export const TRACK_META = {
  placement: { key: 'placement', label: 'Plaatsingen', color: HV2.green, tint: '#e6ece8', icon: 'plaatsing' },
  interview: { key: 'interview', label: 'Interviews', color: HV2.blue, tint: '#e6e9f0', icon: 'interview' },
  // CV Send tekent het entiteitsglyph (document met pijl), geen Material-pad.
  cvsend: { key: 'cvsend', label: 'CV Send', color: HV2.accentSrc, tint: HV2.srcTint, ent: 'cvSend' },
};

// De Material Symbols-paden van de twee trackiconen (gevuld, viewBox 0 -960 960
// 960), letterlijk uit public/icons/plaatsing.svg en interview.svg. Die bestanden
// blijven voor het kantoorscherm (public/screen.html, CSS-mask);
// lib/ui-kit-marks.test.mjs bewaakt dat bestand en pad gelijk blijven.
export const TRACK_ICON_VIEWBOX = '0 -960 960 960';
export const TRACK_ICON_PATHS = {
  plaatsing: 'M80-160v-94q0-34 17-62.5t51-43.5q72-32 132-46t120-14q29 0 61.5 3.5T528-404l-49 49q-20-2-39.5-3.5T400-360q-58 0-105.5 10.5T172-306q-17 8-24.5 23t-7.5 29v34h319l60 60H80Zm545 16L484-285l42-42 99 99 213-213 42 42-255 255ZM292-524q-42-42-42-108t42-108q42-42 108-42t108 42q42 42 42 108t-42 108q-42 42-108 42t-108-42Zm167 304Zm5.5-347.5Q490-593 490-632t-25.5-64.5Q439-722 400-722t-64.5 25.5Q310-671 310-632t25.5 64.5Q361-542 400-542t64.5-25.5ZM400-632Z',
  interview: 'M180-80q-24 0-42-18t-18-42v-620q0-24 18-42t42-18h65v-60h65v60h340v-60h65v60h65q24 0 42 18t18 42v620q0 24-18 42t-42 18H180Zm0-60h600v-430H180v430Zm0-490h600v-130H180v130Zm0 0v-130 130Zm300 230q-17 0-28.5-11.5T440-440q0-17 11.5-28.5T480-480q17 0 28.5 11.5T520-440q0 17-11.5 28.5T480-400Zm-188.5-11.5Q280-423 280-440t11.5-28.5Q303-480 320-480t28.5 11.5Q360-457 360-440t-11.5 28.5Q337-400 320-400t-28.5-11.5ZM640-400q-17 0-28.5-11.5T600-440q0-17 11.5-28.5T640-480q17 0 28.5 11.5T680-440q0 17-11.5 28.5T640-400ZM480-240q-17 0-28.5-11.5T440-280q0-17 11.5-28.5T480-320q17 0 28.5 11.5T520-280q0 17-11.5 28.5T480-240Zm-188.5-11.5Q280-263 280-280t11.5-28.5Q303-320 320-320t28.5 11.5Q360-297 360-280t-11.5 28.5Q337-240 320-240t-28.5-11.5ZM640-240q-17 0-28.5-11.5T600-280q0-17 11.5-28.5T640-320q17 0 28.5 11.5T680-280q0 17-11.5 28.5T640-240Z',
};

// `color` overschrijft de tracktint, bijvoorbeeld HV2.muted voor een gedempte
// archiefvariant van dezelfde track. Accepteert de tracksleutel ('placement',
// 'interview', 'cvsend') of de icoonnaam ('plaatsing').
export function TrackIcon({ track, size, color }) {
  const meta = TRACK_META[track] || (track === 'plaatsing' ? TRACK_META.placement : null);
  if (!meta) return null;
  const px = size || 16;
  if (meta.ent) return <EntityIcon entity={meta.ent} size={px} color={color || meta.color} />;
  return (
    <svg viewBox={TRACK_ICON_VIEWBOX} width={px} height={px} fill={color || meta.color} aria-hidden="true"
      style={{ display: 'inline-block' }}>
      <path d={TRACK_ICON_PATHS[meta.icon]} />
    </svg>
  );
}

// ── Agenda-iconen ───────────────────────────────────────────────────────────
// Type-iconen van een afspraak. Een interview gebruikt het gedeelde
// process-glyph van de CRM-grid en de Vandaag-container; de overige paden zijn
// agenda-iconen. AgendaRow en WeekAgenda lezen ze via components/HomeV2/AgendaRow.js.
export const AGENDA_ICONS = {
  phone: ['M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z'],
  video: ['M23 7l-7 5 7 5V7z', 'M14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z'],
  office: ['M3 21h18M6 21V8l6-4 6 4v13M10 21v-5h4v5'],
  handmade: ['M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z', 'M15 5l4 4'],
  calendar: ['M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z', 'M8 3v4M16 3v4M3 11h18'],
  note: ['M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z', 'M14 3v6h6M8 13h8M8 17h5'],
};

export function AgendaGlyph({ kind, size = 15, color }) {
  if (kind === 'process') return <EntityIcon entity="process" size={size} color={color || HV2.mute2} />;
  const paths = AGENDA_ICONS[kind] || AGENDA_ICONS.phone;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color || HV2.mute2} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, display: 'block' }}>
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

// ── Logotegel ──────────────────────────────────────────────────────────────
// Een bedrijfslogo als <img> wanneer er een bron is (in het portaal een data:-URI
// uit de snapshot), anders het neutrale initiaal-tegeltje. CompanyLogo in de CRM
// tekent zijn tegel hiermee, zodat CRM en portaal dezelfde tegel tonen.
// De letter blijft fsBadge tot de tegel groot genoeg is om mee te groeien.
export function LogoTile({ src, name, size = 28, radius = 3, style }) {
  if (src) {
    return (
      <img src={src} alt={name || ''} width={size} height={size}
        style={{ display: 'block', width: size, height: size, borderRadius: radius, flexShrink: 0, objectFit: 'contain', ...style }} />
    );
  }
  return (
    <span aria-hidden="true" style={{
      width: size, height: size, borderRadius: radius, background: HV2.bgSub,
      border: `1px solid ${HV2.border}`, boxSizing: 'border-box', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.max(HV2.fsBadge, Math.round(size * 0.4)), fontWeight: 560, lineHeight: 1, color: HV2.muted, userSelect: 'none',
      ...style,
    }}>{(String(name || '').trim().charAt(0) || '?').toUpperCase()}</span>
  );
}
